import React, { useState, useEffect } from 'react';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';

export interface TabDefinition {
  label: string;
  icon?: React.ReactElement;
  content: React.ReactNode;
}

interface SidebarTabsProps {
  tabs: TabDefinition[];
  defaultTab?: number;
  activeTab?: number;
  onTabChange?: (index: number) => void;
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  tabs: {
    borderRight: `1px solid ${theme.palette.divider}`,
    minWidth: 180,
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
      borderRight: 'none',
      borderBottom: `1px solid ${theme.palette.divider}`,
      minWidth: 'auto',
    },
  },
  tab: {
    textTransform: 'none',
    minHeight: 48,
    alignItems: 'center',
    textAlign: 'center',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(3),
  },
}));

const SidebarTabs: React.FC<SidebarTabsProps> = ({ tabs, defaultTab = 0, activeTab, onTabChange }) => {
  const classes = useStyles();
  const isControlled = activeTab !== undefined;
  const [internalValue, setInternalValue] = useState(defaultTab);
  const value = isControlled ? activeTab : internalValue;
  const [orientation, setOrientation] = useState<'vertical' | 'horizontal'>(
    typeof window !== 'undefined' && window.innerWidth <= 960 ? 'horizontal' : 'vertical'
  );

  useEffect(() => {
    const handleResize = () => {
      setOrientation(window.innerWidth <= 960 ? 'horizontal' : 'vertical');
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Box className={classes.root}>
      <Tabs
        orientation={orientation}
        variant="scrollable"
        value={value}
        onChange={(_, newValue) => {
          if (isControlled && onTabChange) {
            onTabChange(newValue);
          } else {
            setInternalValue(newValue);
          }
        }}
        className={classes.tabs}
        indicatorColor="primary"
      >
        {tabs.map((tab, idx) => (
          <Tab
            key={idx}
            label={tab.label}
            icon={tab.icon}
            className={classes.tab}
            id={`sidebar-tab-${idx}`}
            aria-controls={`sidebar-tabpanel-${idx}`}
          />
        ))}
      </Tabs>
      <div
        className={classes.content}
        role="tabpanel"
        id={`sidebar-tabpanel-${value}`}
        aria-labelledby={`sidebar-tab-${value}`}
      >
        {tabs[value]?.content}
      </div>
    </Box>
  );
};

export default SidebarTabs;
